import { compactCustomFieldValues } from '@/lib/contacts/custom-field-values'
import type {
  ContactFormState,
  CustomFieldFormValue,
  CustomFieldSetupForm,
} from '@/lib/contacts/types'

export function personPayload(form: ContactFormState) {
  const payload = {
    name: form.name,
    email: blankToNull(form.email),
    phone: blankToNull(form.phone),
    vat_number: blankToNull(form.vatNumber ?? ''),
    address_line1: blankToNull(form.addressLine1 ?? ''),
    address_line2: blankToNull(form.addressLine2 ?? ''),
    city: blankToNull(form.city ?? ''),
    region: blankToNull(form.region ?? ''),
    postal_code: blankToNull(form.postalCode ?? ''),
    country_code: blankToNull(form.countryCode ?? ''),
    tax_exempt: form.taxExempt,
    tax_exempt_note: blankToNull(form.taxExemptNote),
    tax_exempt_reason: form.taxExempt ? (form.taxExemptReason ?? 'exempt') : null,
  }

  return withCustomFieldValues(payload, form.customFieldValues)
}

export function organizationPayload(form: ContactFormState) {
  const payload = {
    name: form.name,
    vat_number: blankToNull(form.vatNumber ?? ''),
    address_line1: blankToNull(form.addressLine1 ?? ''),
    address_line2: blankToNull(form.addressLine2 ?? ''),
    city: blankToNull(form.city ?? ''),
    region: blankToNull(form.region ?? ''),
    postal_code: blankToNull(form.postalCode ?? ''),
    country_code: blankToNull(form.countryCode ?? ''),
    tax_exempt: form.taxExempt,
    tax_exempt_note: blankToNull(form.taxExemptNote),
    tax_exempt_reason: form.taxExempt ? (form.taxExemptReason ?? 'exempt') : null,
  }

  return withCustomFieldValues(payload, form.customFieldValues)
}

export function customFieldSetupPayload(form: CustomFieldSetupForm) {
  return {
    label: form.label,
    field_type: form.fieldType,
    applies_to: form.appliesTo,
    field_access: form.fieldAccess,
    ask_on_join_form: form.askOnJoinForm,
    required: form.required,
    choices: customFieldChoices(form),
  }
}

export function customFieldUpdatePayload(form: CustomFieldSetupForm) {
  const payload: {
    applies_to: CustomFieldSetupForm['appliesTo']
    field_access: CustomFieldSetupForm['fieldAccess']
    choices?: string[]
    ask_on_join_form: boolean
    required: boolean
  } = {
    applies_to: form.appliesTo,
    field_access: form.fieldAccess,
    ask_on_join_form: form.askOnJoinForm,
    required: form.required,
  }

  if (form.fieldType === 'single-select' || form.fieldType === 'multi-select') {
    payload.choices = customFieldChoices(form)
  }

  return payload
}

export function withCustomFieldValues<T extends Record<string, unknown>>(
  payload: T,
  values: Record<string, CustomFieldFormValue>,
) {
  const customFieldValues = compactCustomFieldValues(values)
  if (Object.keys(customFieldValues).length === 0) return payload

  return { ...payload, custom_field_values: customFieldValues }
}

export function blankToNull(value: string) {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function customFieldChoices(form: CustomFieldSetupForm) {
  if (form.fieldType !== 'single-select' && form.fieldType !== 'multi-select') return []

  return form.choices
    .split(',')
    .map((choice) => choice.trim())
    .filter(Boolean)
}
